import { useState, useEffect } from "react";
import Modal from "../Modal";
import ListButton from "../components/ListButton";
import MainSaveButton from "../components/MainSaveButton";
import { FaPhone, FaEnvelope, FaWhatsapp, FaPlus, FaTrash } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { BACKEND } from "../../../config/configEnvs";

export default function ClientDataModal({ openModalClientData, setOpenModalClientData, selectedClientData, setSelectedClientData, setClients, setServicioData }) {
  // Initialize arrays from selectedClientData
  const [phoneStorage, setPhoneStorage] = useState([]);
  const [emailStorage, setEmailStorage] = useState([]);
  const [wppStorage, setWppStorage] = useState([]);

  // Track which list is currently adding a new item
  const [addingPhone, setAddingPhone] = useState(false);
  const [addingEmail, setAddingEmail] = useState(false);
  const [addingWpp, setAddingWpp] = useState(false);

  // Temporary values for new items
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newWpp, setNewWpp] = useState("");

  // Loading state for save button
  const [saving, setSaving] = useState(false);

  // Initialize data when modal opens or selectedClientData changes
  useEffect(() => {
    if (openModalClientData) {
      // Preserve existing items with their IDs
      setPhoneStorage(selectedClientData?.phoneStorage || []);
      setEmailStorage(selectedClientData?.emailStorage || []);
      setWppStorage(selectedClientData?.wppStorage || []);
    }
  }, [openModalClientData, selectedClientData]);

  // Reset states when modal closes
  useEffect(() => {
    if (!openModalClientData) {
      setAddingPhone(false);
      setAddingEmail(false);
      setAddingWpp(false);
      setNewPhone("");
      setNewEmail("");
      setNewWpp("");
      setSaving(false);
    }
  }, [openModalClientData]);

  const handleAddPhone = () => {
    if (newPhone.trim()) {
      setPhoneStorage([...phoneStorage, { phone: newPhone.trim() }]);
      setNewPhone("");
      // Keep input open for adding more items
    }
  };

  const handleAddEmail = () => {
    if (newEmail.trim()) {
      setEmailStorage([...emailStorage, { email: newEmail.trim() }]);
      setNewEmail("");
      // Keep input open for adding more items
    }
  };

  const handleAddWpp = () => {
    if (newWpp.trim()) {
      setWppStorage([...wppStorage, { wpp: newWpp.trim() }]);
      setNewWpp("");
      // Keep input open for adding more items
    }
  };

  const handleRemovePhone = (index) => {
    setPhoneStorage(phoneStorage.filter((_, i) => i !== index));
  };

  const handleRemoveEmail = (index) => {
    setEmailStorage(emailStorage.filter((_, i) => i !== index));
  };

  const handleRemoveWpp = (index) => {
    setWppStorage(wppStorage.filter((_, i) => i !== index));
  };

  const handleCancelAdd = (type) => {
    if (type === "phone") {
      setAddingPhone(false);
      setNewPhone("");
    } else if (type === "email") {
      setAddingEmail(false);
      setNewEmail("");
    } else if (type === "wpp") {
      setAddingWpp(false);
      setNewWpp("");
    }
  };

  const handleSave = async () => {
    if (!selectedClientData?.id) {
      toast.error("No se puede guardar: servicio no identificado");
      return;
    }

    setSaving(true);
    try {
      // Send all items (existing with IDs and new without IDs)
      // The backend will assign IDs to new items
      const response = await axios.put(
        `${BACKEND}/api/serviciosCotizaciones/${selectedClientData.id}`,
        {
          phoneStorage,
          emailStorage,
          wppStorage
        }
      );

      // Update selectedClientData with the response from backend
      // The backend returns all items with their IDs assigned
      const updatedData = response.data.doc;
      
      // Update selectedClientData
      if (setSelectedClientData) {
        setSelectedClientData({
          ...selectedClientData,
          phoneStorage: updatedData.phoneStorage || phoneStorage,
          emailStorage: updatedData.emailStorage || emailStorage,
          wppStorage: updatedData.wppStorage || wppStorage,
          ...updatedData
        });
      }

      // Update local state with the IDs returned from backend
      setPhoneStorage(updatedData.phoneStorage || phoneStorage);
      setEmailStorage(updatedData.emailStorage || emailStorage);
      setWppStorage(updatedData.wppStorage || wppStorage);

      // Update clients state to persist changes in the table
      if (setClients && selectedClientData?.cotizacion?.cliente?.id) {
        setClients((prevClients) =>
          prevClients.map((client) => {
            if (client.id !== selectedClientData.cotizacion.cliente.id) return client;
            return {
              ...client,
              services: client.services.map((srv) =>
                srv.id === selectedClientData.id
                  ? {
                      ...srv,
                      phoneStorage: updatedData.phoneStorage || phoneStorage,
                      emailStorage: updatedData.emailStorage || emailStorage,
                      wppStorage: updatedData.wppStorage || wppStorage,
                      ...updatedData
                    }
                  : srv
              ),
            };
          })
        );
      }

      // Update servicioData if provided (for SeguimientoDetails)
      if (setServicioData && selectedClientData?.id) {
        setServicioData((prevServicioData) => {
          if (prevServicioData?.id === selectedClientData.id) {
            return {
              ...prevServicioData,
              phoneStorage: updatedData.phoneStorage || phoneStorage,
              emailStorage: updatedData.emailStorage || emailStorage,
              wppStorage: updatedData.wppStorage || wppStorage,
              ...updatedData
            };
          }
          return prevServicioData;
        });
      }

      toast.success("Datos del cliente guardados correctamente");
      setOpenModalClientData(false);
    } catch (error) {
      console.error("Error al guardar los datos del cliente:", error);
      toast.error("Error al guardar los datos del cliente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      activeModal={openModalClientData}
      onClose={() => {
        setOpenModalClientData(false);
        setAddingPhone(false);
        setAddingEmail(false);
        setAddingWpp(false);
        setNewPhone("");
        setNewEmail("");
        setNewWpp("");
      }}
      title="Datos del cliente"
      className="max-w-6xl"
      centered
      footerContent={
        <div className="flex w-full items-center justify-center">
          <div className="flex w-1/2">
            <MainSaveButton
              text="Guardar"
              onClick={handleSave}
              loading={saving}
              disabled={saving}
              fullWidth={true}
            />
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Phone Storage Section */}
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FaPhone className="text-blue-600" />
            Teléfonos
          </h3>
          
          {/* Existing phones */}
          <div className="max-h-48 overflow-y-auto flex flex-col gap-2">
            {phoneStorage.map((item, index) => (
              <div key={index} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-lg border border-gray-200">
                <span className="flex-1 text-sm text-gray-800">{item.phone}</span>
                <button
                  onClick={() => handleRemovePhone(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Eliminar"
                >
                  <FaTrash className="text-sm" />
                </button>
              </div>
            ))}
          </div>

          {/* Add phone input */}
          {addingPhone ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="Ingrese el teléfono"
                className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddPhone();
                  } else if (e.key === "Escape") {
                    handleCancelAdd("phone");
                  }
                }}
              />
              <button
                onClick={handleAddPhone}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Agregar
              </button>
              <button
                onClick={() => handleCancelAdd("phone")}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <ListButton
              action={() => setAddingPhone(true)}
              content="Agregar teléfono"
              icon={<FaPlus />}
              color="blue"
            />
          )}
        </div>

        {/* Email Storage Section */}
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FaEnvelope className="text-orange-600" />
            Correos electrónicos
          </h3>
          
          {/* Existing emails */}
          <div className="max-h-48 overflow-y-auto flex flex-col gap-2">
            {emailStorage.map((item, index) => (
              <div key={index} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-lg border border-gray-200">
                <span className="flex-1 text-sm text-gray-800">{item.email}</span>
                <button
                  onClick={() => handleRemoveEmail(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Eliminar"
                >
                  <FaTrash className="text-sm" />
                </button>
              </div>
            ))}
          </div>

          {/* Add email input */}
          {addingEmail ? (
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Ingrese el correo electrónico"
                className="flex-1 px-3 py-2 border border-orange-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddEmail();
                  } else if (e.key === "Escape") {
                    handleCancelAdd("email");
                  }
                }}
              />
              <button
                onClick={handleAddEmail}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
              >
                Agregar
              </button>
              <button
                onClick={() => handleCancelAdd("email")}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <ListButton
              action={() => setAddingEmail(true)}
              content="Agregar correo"
              icon={<FaPlus />}
              color="orange"
            />
          )}
        </div>

        {/* WhatsApp Storage Section */}
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FaWhatsapp className="text-emerald-600" />
            WhatsApp
          </h3>
          
          {/* Existing WhatsApp numbers */}
          <div className="max-h-48 overflow-y-auto flex flex-col gap-2">
            {wppStorage.map((item, index) => (
              <div key={index} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-lg border border-gray-200">
                <span className="flex-1 text-sm text-gray-800">{item.wpp}</span>
                <button
                  onClick={() => handleRemoveWpp(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Eliminar"
                >
                  <FaTrash className="text-sm" />
                </button>
              </div>
            ))}
          </div>

          {/* Add WhatsApp input */}
          {addingWpp ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newWpp}
                onChange={(e) => setNewWpp(e.target.value)}
                placeholder="Ingrese el número de WhatsApp"
                className="flex-1 px-3 py-2 border border-green-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddWpp();
                  } else if (e.key === "Escape") {
                    handleCancelAdd("wpp");
                  }
                }}
              />
              <button
                onClick={handleAddWpp}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
              >
                Agregar
              </button>
              <button
                onClick={() => handleCancelAdd("wpp")}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <ListButton
              action={() => setAddingWpp(true)}
              content="Agregar WhatsApp"
              icon={<FaPlus />}
              color="green"
            />
          )}
        </div>
      </div>
    </Modal>
  )
}